import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import {
  AddressParser,
  AddressParserV2,
  IAddressRaw,
} from "@/models/address/address.parser"
import { Account } from "./account"
import { IAccount } from "./account.types"
import dayjs from "dayjs"
import { CustomerParser, ICustomerRaw } from "../customer/customer.parser"
import { safeDayjs } from "@/helpers/dateHelper"
import {
  AccountDetailsResponse,
  PropertyAccount,
} from "@/api/backofficeRest/generated/model"

type SupplyAddressRaw = {
  address: IAddressRaw
}

export interface IAccountDetailsRaw {
  id: number
  number: string
  fromDt: string
  createdDttm: string
  balance: number
  name: string
  billingAddress: IAddressRaw
  junifer_customer_id: number
  toDt?: string
  cancelled?: boolean
  customer?: ICustomerRaw
  supply_address?: SupplyAddressRaw | []
}

export interface IAccountRaw {
  id: number
  details: IAccountDetailsRaw
  junifer_property?: {
    id: number
    address: IAddressRaw
  }
}

/**
 * To be removed after no longer in use
 */
export const AccountParser: IParser<IAccount, IAccountRaw> = {
  toDomain: (data: IAccountRaw) => {
    try {
      const supplyAddress =
        data.junifer_property?.address ??
        data.details.supply_address?.["address"]

      return new Account(
        data.details.number,
        data.details.id,
        data.details.name,
        data.details.balance,
        dayjs(data.details.createdDttm),
        dayjs(data.details.fromDt),
        AddressParser.toDomain(data.details.billingAddress),
        data.details.junifer_customer_id,
        safeDayjs(data.details.toDt),
        supplyAddress ? AddressParser.toDomain(supplyAddress) : undefined,
        data.id,
        data.junifer_property?.id,
        data.details?.cancelled,
        data.details?.customer
          ? CustomerParser.toDomain(data.details.customer)
          : undefined,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
} as IParser<IAccount, IAccountRaw>

/**
 * Parses hasura account details from GBO to domain format
 */
export const AccountParserV2: IParser<IAccount, AccountDetailsResponse> = {
  toDomain: (data) => {
    try {
      const details = data?.details
      if (!details) {
        throw new Error("No details or address found for account")
      }

      return new Account(
        details.number ?? "",
        details.id ?? 0,
        details.name ?? "",
        details.balance ?? 0,
        dayjs(details.createdDttm),
        dayjs(details.fromDt),
        details.billingAddress
          ? AddressParserV2.toDomain(details.billingAddress)
          : undefined,
        undefined,
        safeDayjs(details.toDt),
        details.supplyAddress
          ? AddressParserV2.toDomain(details.supplyAddress)
          : undefined,
        data.id,
        data.juniferProperty?.id,
        details?.cancelled ?? undefined,
        undefined,
        details.firstName ?? undefined,
        details.lastName ?? undefined,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}

/**
 * Parses one account that has held a site's Junifer property (current or
 * ceased) into the domain Account. This endpoint carries only occupancy
 * fields, so balance/addresses/customer are absent; consumers of
 * supplyAddressAccounts read number, startDate and endDate. Junifer's
 * open-ended "9999-..." toDttm sentinel means the account still holds the
 * property, so it maps to no endDate (i.e. the current occupant).
 */
export const PropertyAccountParser: IParser<IAccount, PropertyAccount> = {
  toDomain: (data: PropertyAccount) => {
    try {
      const endDate = data.toDttm?.startsWith("9999")
        ? undefined
        : safeDayjs(data.toDttm)
      return new Account(
        data.accountNumber ?? "",
        data.accountId,
        data.name ?? "",
        0,
        dayjs(data.fromDttm),
        dayjs(data.fromDttm),
        undefined,
        undefined,
        endDate,
        undefined,
        undefined,
        undefined,
        data.cancelled ?? undefined,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
